<template>
    <div class="multi-asset-cell" @click="openDetail">
        <div v-if="assets && assets.length > 0" class="cell-preview">
            <div class="first-asset">
                <img
                    v-if="isImageType && getAssetUrl(assets[0])"
                    :src="getAssetUrl(assets[0])"
                    class="preview-thumb"
                />
                <md-icon v-else :style="{ color: getFileColor(assets[0]) }">{{ cellIcon }}</md-icon>
                <span class="first-name" v-if="!isImageType">{{ assets[0].name }}</span>
            </div>
            <span v-if="assets.length > 1" class="asset-badge">{{
                $t("table.multiAssetMoreCount", { count: assets.length - 1 })
            }}</span>
        </div>
        <PermissionController :permissions="['table.update']" v-else>
            <div class="upload-placeholder" @click.stop="openDetail">
                <md-icon>{{ uploadIcon }}</md-icon>
            </div>
        </PermissionController>

        <Modal2
            v-if="detailVisible"
            @cancel="closeDetail"
            @ok="saveChanges"
            :title="field.name"
            :okText="$t('message.save')"
            :loading="saving"
            className="multi-asset-detail-modal keydown-prevent-modal"
        >
            <div class="detail-content" @click.stop>
                <div class="ma-modal-mode-tabs" v-if="hasGalleryPreview" @click.stop>
                    <a-radio-group v-model="mediaModalMode" button-style="solid">
                        <a-radio-button value="preview">{{ $t("table.galleryPreview") }}</a-radio-button>
                        <a-radio-button value="edit">{{ $t("table.editMedia") }}</a-radio-button>
                    </a-radio-group>
                </div>

                <div v-if="hasGalleryPreview && mediaModalMode === 'preview'" class="ma-preview-pane">
                    <div class="ma-files-grid" v-if="localAssets.length > 0">
                        <div
                            class="ma-file-card"
                            v-for="(asset, idx) in localAssets"
                            :key="asset._id || idx"
                            @click.stop="openMediaGallery(idx)"
                        >
                            <div class="ma-file-icon">
                                <img
                                    v-if="isMultiImageField && getAssetUrl(asset)"
                                    :src="getAssetUrl(asset)"
                                />
                                <a-icon
                                    v-else-if="isMultiVideoField"
                                    type="play-circle"
                                    class="ma-preview-type-icon ma-preview-type-icon-video"
                                />
                                <a-icon
                                    v-else-if="isMultiAudioField"
                                    type="sound"
                                    class="ma-preview-type-icon ma-preview-type-icon-audio"
                                />
                                <a-icon
                                    v-else
                                    :type="previewCardAntIcon(asset)"
                                    class="ma-preview-type-icon ma-preview-type-icon-file"
                                />
                            </div>
                            <div class="ma-file-name" :title="asset.name">{{ asset.name }}</div>
                        </div>
                    </div>
                    <div class="ma-empty-preview" v-else>
                        <a-icon :type="emptyPreviewAntIcon" />
                        <p>{{ emptyPreviewText }}</p>
                    </div>
                </div>

                <template v-else>
                <div class="detail-assets-grid">
                    <div
                        v-for="(asset, index) in localAssets"
                        :key="asset._id || index"
                        class="detail-asset-chip"
                    >
                        <img
                            v-if="isImageType && getAssetUrl(asset)"
                            :src="getAssetUrl(asset)"
                            class="chip-thumb"
                        />
                        <md-icon v-else-if="isVideoType" class="chip-icon" style="color: #1890ff" @click.native.stop="showVideoModal(asset)">play_circle_outline</md-icon>
                        <div v-else-if="isAudioType" class="chip-audio" @click.stop="toggleAudio(asset._id)">
                            <md-icon>{{ audioStates[asset._id] ? 'pause_circle' : 'play_circle' }}</md-icon>
                        </div>
                        <md-icon v-else class="chip-icon" :style="{ color: getFileColor(asset) }">{{ getFileIcon(asset) }}</md-icon>
                        <a
                            v-if="!isImageType && !isVideoType && !isAudioType"
                            :href="getAssetUrl(asset)"
                            target="_blank"
                            download
                            class="chip-name chip-link"
                            @click.stop
                        >{{ asset.name }}</a>
                        <span v-else class="chip-name">{{ asset.name }}</span>
                        <audio v-if="isAudioType" :ref="'audio_' + asset._id" :src="getAssetUrl(asset)" @ended="audioStates[asset._id] = false"></audio>
                        <PermissionController :permissions="['table.update']" v-if="!readonly">
                            <div class="chip-remove" @click.stop="removeLocalAsset(index)">
                                <md-icon>close</md-icon>
                            </div>
                        </PermissionController>
                    </div>
                </div>

                <PermissionController :permissions="['table.update']" v-if="!readonly">
                    <a-upload
                        :before-upload="beforeUpload"
                        :custom-request="uploadFileLocal"
                        :show-upload-list="false"
                        :accept="acceptedFileTypes"
                        :multiple="true"
                        class="detail-upload"
                    >
                        <a-button icon="plus" :loading="uploading">
                            {{ $t('table.addMore') }}
                        </a-button>
                    </a-upload>
                </PermissionController>
                </template>
            </div>
        </Modal2>

        <MultiAssetMediaPreviewOverlay
            v-if="galleryVisible && hasGalleryPreview && localAssets.length > 0"
            :variant="galleryVariant"
            :assets="localAssets"
            :initial-index="galleryIndex"
            @close="closeMediaGallery"
        />

        <Modal2
            v-if="showVideoPlayerModal && activeVideoAsset"
            @cancel="closeVideoModal"
            :show-footer="false"
            :show-title="false"
            className="asset-viewer-modal keydown-prevent-modal"
        >
            <video-player
                class="modal-video-player"
                ref="modalVideoPlayer"
                :options="playerOptions(activeVideoAsset)"
                :playsinline="true"
            ></video-player>
        </Modal2>
    </div>
</template>

<script>
import Vue from "vue";
import { uploadAsset } from "../../../../../api/assetUpload";
import axios from "axios";
import "video.js/dist/video-js.css";
import { videoPlayer } from "vue-video-player";
import Modal2 from "../../../../../components/common/Modal2.vue";
import MultiAssetMediaPreviewOverlay from "./MultiAssetMediaPreviewOverlay.vue";

export default Vue.extend({
    name: "MultiAssetCell",
    components: {
        Modal2,
        videoPlayer,
        MultiAssetMediaPreviewOverlay,
    },
    props: {
        record: {
            type: Object,
            required: true,
        },
        field: {
            type: Object,
            required: true,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            detailVisible: false,
            showVideoPlayerModal: false,
            activeVideoAsset: null,
            audioStates: {},
            uploadingCount: 0,
            saving: false,
            localAssets: [],
            mediaModalMode: "preview",
            galleryVisible: false,
            galleryIndex: 0,
        };
    },
    computed: {
        assets() {
            var val = this.record.data[this.field._id];
            return Array.isArray(val) ? val : [];
        },
        assetUrls() {
            return this.assets.map(this.getAssetUrl);
        },
        baseType() {
            var typeMap = {
                "multi-file": "file",
                "multi-image": "image",
                "multi-video": "video",
                "multi-audio": "audio",
            };
            return typeMap[this.field.fieldType] || "file";
        },
        isImageType() {
            return this.baseType === "image";
        },
        isVideoType() {
            return this.baseType === "video";
        },
        isAudioType() {
            return this.baseType === "audio";
        },
        cellIcon() {
            var iconMap = {
                "multi-file": "folder",
                "multi-video": "video_library",
                "multi-image": "photo_library",
                "multi-audio": "library_music",
            };
            return iconMap[this.field.fieldType] || "folder";
        },
        acceptedFileTypes() {
            var fileTypes = {
                "multi-file": "*",
                "multi-video": "video/*",
                "multi-image": "image/*",
                "multi-audio": "audio/*",
            };
            return fileTypes[this.field.fieldType] || "*";
        },
        uploadIcon() {
            var fileTypeIcon = {
                "multi-file": "folder",
                "multi-video": "video_library",
                "multi-image": "photo_library",
                "multi-audio": "library_music",
            };
            return fileTypeIcon[this.field.fieldType] || "folder";
        },
        uploading() {
            return this.uploadingCount > 0;
        },
        isMultiImageField() {
            return this.field.fieldType === "multi-image";
        },
        isMultiVideoField() {
            return this.field.fieldType === "multi-video";
        },
        isMultiAudioField() {
            return this.field.fieldType === "multi-audio";
        },
        isMultiFileField() {
            return this.field.fieldType === "multi-file";
        },
        hasGalleryPreview() {
            return (
                this.isMultiImageField ||
                this.isMultiVideoField ||
                this.isMultiAudioField ||
                this.isMultiFileField
            );
        },
        galleryVariant() {
            if (this.isMultiImageField) return "image";
            if (this.isMultiVideoField) return "video";
            if (this.isMultiAudioField) return "audio";
            return "file";
        },
        emptyPreviewAntIcon() {
            if (this.isMultiImageField) return "picture";
            if (this.isMultiVideoField) return "video-camera";
            if (this.isMultiAudioField) return "sound";
            return "folder";
        },
        emptyPreviewText() {
            if (this.isMultiImageField) return this.$t("table.noImagesInGallery");
            if (this.isMultiVideoField) return this.$t("table.noVideosInGallery");
            if (this.isMultiAudioField) return this.$t("table.noAudioInGallery");
            return this.$t("table.noFilesInGallery");
        },
    },
    methods: {
        getAssetUrl(asset) {
            if (!asset) return "";
            return asset.cdnUrl
                ? asset.cdnUrl
                : "https://static.supsis.live/" + asset.path + asset._id + "." + asset.ext;
        },
        getFileIcon(asset) {
            if (!asset) return "insert_drive_file";
            var ext = asset.ext;
            if (ext === "doc" || ext === "docx") return "description";
            if (ext === "xls" || ext === "xlsx") return "grid_on";
            if (ext === "pdf") return "picture_as_pdf";
            return "insert_drive_file";
        },
        getFileColor(asset) {
            var colors = ["#FF6B6B", "#4ECDC4", "#1A535C", "#FF7F50", "#6495ED", "#9FE2BF"];
            if (!asset || !asset._id) return colors[0];
            var hash = 0;
            var idStr = asset._id.toString();
            for (var i = 0; i < idStr.length; i++) {
                hash = idStr.charCodeAt(i) + ((hash << 5) - hash);
            }
            return colors[Math.abs(hash) % colors.length];
        },
        openDetail() {
            this.localAssets = this.assets.map(function (a) { return Object.assign({}, a); });
            this.mediaModalMode = this.hasGalleryPreview ? "preview" : "edit";
            this.galleryVisible = false;
            this.detailVisible = true;
        },
        closeDetail() {
            this.galleryVisible = false;
            this.localAssets = [];
            this.detailVisible = false;
        },
        openMediaGallery(index) {
            this.galleryIndex = index;
            this.galleryVisible = true;
        },
        closeMediaGallery() {
            this.galleryVisible = false;
        },
        previewCardAntIcon(asset) {
            var mime = asset && asset.mime ? asset.mime : "";
            if (mime.indexOf("image") !== -1) return "file-image";
            if (mime.indexOf("video") !== -1) return "video-camera";
            if (mime.indexOf("audio") !== -1) return "sound";
            if (mime.indexOf("pdf") !== -1) return "file-pdf";
            if (mime.indexOf("spreadsheet") !== -1 || mime.indexOf("excel") !== -1) return "file-excel";
            if (mime.indexOf("word") !== -1 || mime.indexOf("document") !== -1) return "file-word";
            var ext = asset && asset.ext ? String(asset.ext).toLowerCase() : "";
            if (ext === "pdf") return "file-pdf";
            if (ext === "xls" || ext === "xlsx") return "file-excel";
            if (ext === "doc" || ext === "docx") return "file-word";
            if (ext === "zip" || ext === "rar") return "file-zip";
            return "file";
        },
        beforeUpload(file) {
            var baseType = this.baseType;
            var isValidType = file.type.indexOf(baseType) !== -1 || baseType === "file";
            if (!isValidType) {
                this.$message.error(this.$t("table.invalidFileType"));
                return false;
            }
            return true;
        },
        async uploadFileLocal(_ref) {
            var file = _ref.file;
            this.uploadingCount++;
            var siteId = this.$store.state.site.site._id;
            var path = siteId + "/table-assets/";
            try {
                var uploadData = await uploadAsset({
                    path: path,
                    name: file.name,
                    size: file.size,
                    type: "table-asset",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });
                await axios({
                    method: uploadData.data.method,
                    url: uploadData.data.url,
                    data: file,
                    headers: { "Content-Type": "application/octet-stream" },
                });
                this.localAssets = [].concat(this.localAssets, [uploadData.data.asset]);
            } catch (error) {
                console.error("Multi asset upload error:", error);
                this.$message.error(this.$t("table.uploadError"));
            } finally {
                this.uploadingCount--;
            }
        },
        removeLocalAsset(index) {
            if (this.readonly) return;
            var copy = [].concat(this.localAssets);
            copy.splice(index, 1);
            this.localAssets = copy;
        },
        saveChanges() {
            this.saving = true;
            var assetIds = this.localAssets.map(function (a) { return a._id || a; });
            this.$emit("update-record", {
                recordId: this.record._id,
                fieldId: this.field._id,
                value: assetIds,
            });
            this.saving = false;
            this.detailVisible = false;
            this.localAssets = [];
        },
        showVideoModal(asset) {
            this.activeVideoAsset = asset;
            this.showVideoPlayerModal = true;
        },
        closeVideoModal() {
            this.showVideoPlayerModal = false;
            this.activeVideoAsset = null;
        },
        playerOptions(asset) {
            return {
                muted: false,
                language: "tr",
                playbackRates: [0.7, 1.0, 1.5, 2.0],
                sources: [
                    {
                        type: asset.mime,
                        src: this.getAssetUrl(asset),
                    },
                ],
                poster: undefined,
                width: 600,
                height: 300,
            };
        },
        toggleAudio(assetId) {
            var refKey = "audio_" + assetId;
            var audioEl = this.$refs[refKey];
            if (Array.isArray(audioEl)) audioEl = audioEl[0];
            if (!audioEl) return;
            if (audioEl.paused) {
                audioEl.play();
                this.$set(this.audioStates, assetId, true);
            } else {
                audioEl.pause();
                this.$set(this.audioStates, assetId, false);
            }
        },
    },
});
</script>

<style scoped lang="scss">
.multi-asset-cell {
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    cursor: pointer;

    .cell-preview {
        display: flex;
        align-items: center;
        gap: 6px;
        max-width: 100%;
        overflow: hidden;

        .first-asset {
            display: flex;
            align-items: center;
            gap: 4px;
            min-width: 0;

            .preview-thumb {
                width: 28px;
                height: 28px;
                object-fit: cover;
                border-radius: 4px;
                flex-shrink: 0;
            }

            .first-name {
                font-size: 12px;
                max-width: 80px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }

        .asset-badge {
            flex-shrink: 0;
            background: #1890ff;
            color: #fff;
            font-size: 11px;
            font-weight: 600;
            padding: 1px 6px;
            border-radius: 10px;
            line-height: 18px;
        }
    }

    .upload-placeholder {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: #bfbfbf;

        &:hover {
            color: #1890ff;
        }
    }
}

.detail-content {
    .ma-modal-mode-tabs {
        margin-bottom: 16px;
    }

    .ma-preview-pane {
        min-height: 120px;
    }

    .ma-files-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 16px;
    }

    .ma-file-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 12px;
        background: var(--color-bg-default, #fff);
        border: 1px solid var(--color-border-default, #e8e8e8);
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
            border-color: #4285f4;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .ma-file-icon {
            width: 56px;
            height: 56px;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #5f6368;

            img {
                width: 56px;
                height: 56px;
                object-fit: cover;
                border-radius: 4px;
            }

            .ma-preview-type-icon {
                font-size: 44px;
            }

            .ma-preview-type-icon-video {
                color: #1890ff;
            }

            .ma-preview-type-icon-audio {
                color: #52c41a;
            }

            .ma-preview-type-icon-file {
                color: #5f6368;
            }
        }

        .ma-file-name {
            font-size: 12px;
            font-weight: 500;
            text-align: center;
            word-break: break-word;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            max-width: 100%;
        }
    }

    .ma-empty-preview {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 32px;
        color: var(--color-text-secondary, #999);

        .anticon {
            font-size: 40px;
            margin-bottom: 8px;
        }

        p {
            margin: 0;
            font-size: 14px;
        }
    }

    .detail-assets-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 16px;

        .detail-asset-chip {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 4px 10px 4px 4px;
            border-radius: 20px;
            background: #f5f5f5;
            border: 1px solid #e8e8e8;
            position: relative;
            max-width: 220px;

            .chip-thumb {
                width: 28px;
                height: 28px;
                object-fit: cover;
                border-radius: 50%;
                flex-shrink: 0;
            }

            .chip-icon {
                flex-shrink: 0;
                font-size: 20px !important;
                margin-left: 4px;
            }

            .chip-audio {
                cursor: pointer;
                display: flex;
                align-items: center;
                flex-shrink: 0;
                margin-left: 4px;
            }

            .chip-name {
                font-size: 12px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                min-width: 0;
            }

            .chip-link {
                color: #1890ff;
                text-decoration: none;

                &:hover {
                    text-decoration: underline;
                }
            }

            audio {
                display: none;
            }

            .chip-remove {
                flex-shrink: 0;
                width: 20px;
                height: 20px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                border-radius: 50%;
                background: rgba(0, 0, 0, 0.15);
                transition: background 0.2s;
                margin-left: 2px;

                &:hover {
                    background: rgba(255, 77, 79, 0.9);
                }

                .md-icon {
                    font-size: 14px !important;
                    width: 14px !important;
                    min-width: 14px !important;
                    height: 14px !important;
                    line-height: 14px !important;
                    color: white;
                }
            }
        }
    }

    .detail-upload {
        display: block;
        margin-top: 8px;
    }
}
</style>

<style lang="scss">
.multi-asset-detail-modal {
    .ant-modal {
        width: 600px !important;
    }
}
.asset-viewer-modal {
    .ant-modal {
        width: 800px !important;

        .modal-video-player {
            width: 100%;
            height: 100%;
        }
    }
}
</style>
